'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SwipeableDrawer } from '@mui/material'
import { useTranslations } from 'next-intl'

import { useSearchInputManager } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { getFormattedDate } from '@/src/utils/date'

import { AirportSelector } from '@/src/components/mobile/srp/searchHeader/components/airportSelector'
import { DateSelector } from '@/src/components/mobile/srp/searchHeader/components/dateSelector'
import { TravelersSelector } from '@/src/components/mobile/srp/searchHeader/components/travelersSelector'

import { MultiCity } from './multiCity'

interface MultiCitySearchFormProps {
    searchManager: ReturnType<typeof useSearchInputManager>
    showError: (message: string) => void
    onSearch: () => void
    onSearchAnalytics?: () => void
    searchButtonLabel?: string
    searchButtonVariant?: 'contained' | 'customVariantPrimary'
    searchButtonDisabled?: boolean
    showNonStop?: boolean
    hideSearchButton?: boolean
    compactTypography?: boolean
}

const MultiCitySearchForm: React.FC<MultiCitySearchFormProps> = ({
    searchManager,
    showError,
    onSearch,
    onSearchAnalytics,
    searchButtonLabel,
    showNonStop = true,
    searchButtonVariant,
    searchButtonDisabled = false,
    hideSearchButton = false,
    compactTypography = false
}) => {
    const {
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        multiCitySegments,
        handleMultiCitySegmentUpdate,
        handleMultiCitySwap,
        handleAddMultiCitySegment,
        handleRemoveMultiCitySegment,
        getAirportsForMultiCitySegment,
        canAddMoreSegment,
        isNonStop,
        handleNonStopChange,
        hasDuplicateSectors
    } = searchManager

    const t = useTranslations('common')
    const { formatDate } = useFormatDate()

    const flightClass = Array.isArray(flightClassState) ? flightClassState[0] : flightClassState

    const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(null)
    const [activeField, setActiveField] = useState<'from' | 'to' | 'date' | null>(null)
    const [airportDrawerOpen, setAirportDrawerOpen] = useState(false)
    const [dateDrawerOpen, setDateDrawerOpen] = useState(false)
    const [travelersDrawerOpen, setTravelersDrawerOpen] = useState(false)

    const segmentsWithSameAirportError = useMemo(() => {
        const errors = new Set<number>()
        multiCitySegments.forEach((segment, index) => {
            const { airportCode: fromCode } = segment.from
            const { airportCode: toCode } = segment.to
            if (fromCode && toCode && fromCode === toCode) {
                errors.add(index)
            }
        })
        return errors
    }, [multiCitySegments])

    useEffect(() => {
        if (segmentsWithSameAirportError.size > 0) {
            showError(t('sameAirportError'))
        }
    }, [segmentsWithSameAirportError.size, showError, t])

    const activeSegment =
        activeSegmentIndex !== null && activeSegmentIndex < multiCitySegments.length
            ? multiCitySegments[activeSegmentIndex]
            : null

    const minDate = useMemo(() => {
        if (activeSegmentIndex === null || activeSegmentIndex <= 0) return null
        for (let i = activeSegmentIndex - 1; i >= 0; i--) {
            if (multiCitySegments[i]?.date) return multiCitySegments[i].date
        }
        return null
    }, [multiCitySegments, activeSegmentIndex])

    const closeFieldEditor = useCallback(() => {
        setAirportDrawerOpen(false)
        setDateDrawerOpen(false)
        setActiveSegmentIndex(null)
        setActiveField(null)
    }, [])

    const formatDateDisplay = useCallback(
        (date: Date | null) => {
            if (!date) return t('selectDate')
            return formatDate(getFormattedDate(date, 'MM/DD/YYYY'), 'ddd, DD MMM')
        },
        [formatDate, t]
    )

    const formatTravelersDisplay = useCallback(() => {
        const total = travelersState.adults + travelersState.childs + travelersState.infants
        return `${total} ${t('traveller', { s: total > 1 ? 's' : '' })}`
    }, [travelersState, t])

    const handleSegmentFieldClick = useCallback((segmentIndex: number, field: 'from' | 'to' | 'date') => {
        setActiveSegmentIndex(segmentIndex)
        setActiveField(field)
        if (field === 'date') {
            setDateDrawerOpen(true)
        } else {
            setAirportDrawerOpen(true)
        }
    }, [])

    const handleAirportSelect = useCallback(
        (value: string, _type: 'from' | 'to') => {
            if (activeSegmentIndex === null || !activeField || activeField === 'date') return
            handleMultiCitySegmentUpdate(activeSegmentIndex, activeField, value)
            closeFieldEditor()
        },
        [activeSegmentIndex, activeField, handleMultiCitySegmentUpdate, closeFieldEditor]
    )

    const handleDateSelect = useCallback(
        (date: Date | null) => {
            if (activeSegmentIndex === null) return
            handleMultiCitySegmentUpdate(activeSegmentIndex, 'date', date)
            closeFieldEditor()
        },
        [activeSegmentIndex, handleMultiCitySegmentUpdate, closeFieldEditor]
    )

    const handleSearchClick = useCallback(() => {
        if (segmentsWithSameAirportError.size > 0) {
            showError(t('sameAirportError'))
            return
        }

        const hasEmptyFields = multiCitySegments.some(
            segment => !segment.from.airportCode || !segment.to.airportCode || !segment.date
        )
        if (hasEmptyFields) {
            showError(t('pleaseFillAllRequiredFields'))
            return
        }

        // Don't search if two journeys share the same sector (e.g. RUH->JED repeated)
        if (hasDuplicateSectors) {
            showError(t('duplicateSectorError'))
            return
        }

        onSearchAnalytics?.()
        onSearch()
    }, [
        segmentsWithSameAirportError.size,
        hasDuplicateSectors,
        multiCitySegments,
        showError,
        t,
        onSearchAnalytics,
        onSearch
    ])

    const airportSelectorProps = useMemo(() => {
        if (!activeSegment || !activeField || activeField === 'date') {
            return { isPopularAirports: true, type: 'from' as const, value: '', loading: false, options: [] }
        }
        const segmentField = activeSegment[activeField]
        return {
            isPopularAirports: segmentField.isPopularAirports,
            type: activeField,
            value: segmentField.value,
            loading: segmentField.loading,
            options: segmentField.options
        }
    }, [activeSegment, activeField])

    const handleAirportSearch = useCallback(
        (query: string, _type: 'from' | 'to') => {
            if (activeSegmentIndex === null || !activeField || activeField === 'date') return
            getAirportsForMultiCitySegment(query, activeSegmentIndex, activeField)
        },
        [activeSegmentIndex, activeField, getAirportsForMultiCitySegment]
    )

    const dateRange = activeSegment?.date
        ? { from: activeSegment.date, to: activeSegment.date, enteredTo: activeSegment.date }
        : { from: new Date(), to: new Date(), enteredTo: new Date() }

    return (
        <>
            <MultiCity
                segments={multiCitySegments}
                segmentsWithSameAirportError={segmentsWithSameAirportError}
                onSegmentFieldClick={handleSegmentFieldClick}
                onSwapClick={handleMultiCitySwap}
                onAddMoreClick={handleAddMultiCitySegment}
                onRemoveSegment={handleRemoveMultiCitySegment}
                onTravelersClick={() => setTravelersDrawerOpen(true)}
                onSearchClick={handleSearchClick}
                formatDateDisplay={formatDateDisplay}
                formatTravelersDisplay={formatTravelersDisplay}
                flightClass={flightClass}
                canAddMore={canAddMoreSegment}
                isNonStop={isNonStop}
                onNonStopChange={handleNonStopChange}
                searchButtonLabel={searchButtonLabel}
                searchButtonVariant={searchButtonVariant}
                searchButtonDisabled={searchButtonDisabled}
                showNonStop={showNonStop}
                hideSearchButton={hideSearchButton}
                compactTypography={compactTypography}
                t={t}
            />

            <SwipeableDrawer
                anchor="right"
                open={airportDrawerOpen}
                onClose={closeFieldEditor}
                onOpen={() => setAirportDrawerOpen(true)}
                slotProps={{ paper: { sx: { width: '100%', height: '100%' } } }}
                ModalProps={{ keepMounted: false }}
            >
                <AirportSelector
                    isPopularAirports={airportSelectorProps.isPopularAirports}
                    type={airportSelectorProps.type}
                    value={airportSelectorProps.value}
                    loading={airportSelectorProps.loading}
                    options={airportSelectorProps.options}
                    onSearch={handleAirportSearch}
                    onSelect={handleAirportSelect}
                    visible
                    onClose={closeFieldEditor}
                />
            </SwipeableDrawer>

            {dateDrawerOpen && (
                <DateSelector
                    open={dateDrawerOpen}
                    onOpen={() => setDateDrawerOpen(true)}
                    onClose={closeFieldEditor}
                    isRoundTrip={false}
                    range={dateRange}
                    onDateSelect={range => handleDateSelect(range?.from || null)}
                    minDate={minDate}
                />
            )}

            <TravelersSelector
                open={travelersDrawerOpen}
                onOpen={() => setTravelersDrawerOpen(true)}
                onClose={() => setTravelersDrawerOpen(false)}
                adults={travelersState.adults}
                children={travelersState.childs}
                infants={travelersState.infants}
                flightClass={flightClass}
                onTravelerChange={handleTravelerChange}
                onFlightClassChange={handleFlightClassChange}
                canIncreaseAdults={canIncreaseAdults(travelersState)}
                canIncreaseChildren={canIncreaseChildren(travelersState)}
                canIncreaseInfants={canIncreaseInfants(travelersState)}
                canDecreaseAdults={canDecreaseAdults(travelersState)}
                canDecreaseChildren={canDecreaseChildren(travelersState)}
                canDecreaseInfants={canDecreaseInfants(travelersState)}
                onApply={() => setTravelersDrawerOpen(false)}
            />
        </>
    )
}

export { MultiCitySearchForm }
export type { MultiCitySearchFormProps }
export default MultiCitySearchForm
